import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit2, Trash2, Shield, User, RotateCcw, KeyRound, Eye, EyeOff } from 'lucide-react';

const AdminUsersManagementPage = () => {
  const { admin, token } = useAuth();
  const { toast } = useToast();
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'standard',
  });

  // Password change modal state
  const [pwdTarget, setPwdTarget] = useState(null);
  const [newPwd, setNewPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const fetchAdminUsers = async () => {
    try {
      const data = await api.getAdminUsers(token);
      setAdminUsers(data.admins || []);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        // Don't send password if editing (unless we add password change field)
        const { password, ...updateData } = formData;
        await api.updateAdminUser(token, editingUser.id, updateData);
        toast({
          title: 'Succès',
          description: 'Administrateur mis à jour',
        });
      } else {
        await api.createAdminUser(token, formData);
        toast({
          title: 'Succès',
          description: 'Administrateur créé',
        });
      }
      
      resetForm();
      fetchAdminUsers();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      password: '', // Don't pre-fill password
      role: user.role,
    });
    setShowForm(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Désactiver cet administrateur ?')) return;

    try {
      await api.deleteAdminUser(token, userId);
      toast({
        title: 'Succès',
        description: 'Administrateur désactivé',
      });
      fetchAdminUsers();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  const handleReactivate = async (userId) => {
    if (!window.confirm('Réactiver cet administrateur ?')) return;

    try {
      await api.updateAdminUser(token, userId, { status: 'active' });
      toast({
        title: 'Succès',
        description: 'Administrateur réactivé',
      });
      fetchAdminUsers();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  const openPwdModal = (user) => {
    setPwdTarget(user);
    setNewPwd('');
    setShowPwd(false);
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) {
      toast({
        title: 'Erreur',
        description: 'Le mot de passe doit contenir au moins 8 caractères',
        variant: 'destructive',
      });
      return;
    }
    setPwdSaving(true);
    try {
      await api.updateAdminPassword(token, pwdTarget.id, newPwd);
      toast({
        title: 'Succès',
        description: `Mot de passe mis à jour pour ${pwdTarget.email}`,
      });
      setPwdTarget(null);
      setNewPwd('');
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setPwdSaving(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      role: 'standard',
    });
  };

  // Check if current admin is principal
  const isPrincipal = admin?.role === 'principal';

  if (!isPrincipal) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Accès Restreint
            </h2>
            <p className="text-gray-600">
              Seuls les administrateurs principaux peuvent gérer les utilisateurs.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion des Administrateurs</h1>
        <Button onClick={() => setShowForm(!showForm)} className="bg-green-600 hover:bg-green-700">
          <Plus className="h-4 w-4 mr-2" />
          Nouvel Admin
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingUser ? 'Modifier l\'administrateur' : 'Créer un nouvel administrateur'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prénom
                  </label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom
                  </label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mot de passe
                  </label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                    minLength={8}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rôle
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                >
                  <option value="standard">Standard</option>
                  <option value="principal">Principal</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Principal: accès complet. Standard: gestion clients/avis/partenaires uniquement.
                </p>
              </div>

              <div className="flex space-x-2">
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  {editingUser ? 'Mettre à jour' : 'Créer'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Liste des Administrateurs ({adminUsers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {adminUsers.map((user) => (
              <div
                key={user.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center space-x-4 min-w-0 flex-1">
                  <div
                    className={`p-3 rounded-full shrink-0 ${
                      user.role === 'principal' ? 'bg-purple-100' : 'bg-blue-100'
                    }`}
                  >
                    {user.role === 'principal' ? (
                      <Shield className="h-6 w-6 text-purple-600" />
                    ) : (
                      <User className="h-6 w-6 text-blue-600" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {user.first_name} {user.last_name}
                    </h3>
                    <p className="text-sm text-gray-600 truncate">{user.email}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          user.role === 'principal'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {user.role === 'principal' ? 'Principal' : 'Standard'}
                      </span>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          user.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {user.status === 'active' ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2 self-end sm:self-auto shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(user)}
                    disabled={user.id === admin?.id}
                    data-testid={`btn-edit-admin-${user.id}`}
                    title="Modifier"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openPwdModal(user)}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                    data-testid={`btn-password-admin-${user.id}`}
                    title="Changer le mot de passe"
                  >
                    <KeyRound className="h-4 w-4" />
                  </Button>
                  {user.status === 'inactive' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReactivate(user.id)}
                      disabled={user.id === admin?.id}
                      className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-300"
                      data-testid={`btn-reactivate-admin-${user.id}`}
                      title="Réactiver"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(user.id)}
                      disabled={user.id === admin?.id}
                      className="text-red-600 hover:text-red-700"
                      data-testid={`btn-deactivate-admin-${user.id}`}
                      title="Désactiver"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Password change modal */}
      {pwdTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !pwdSaving && setPwdTarget(null)}
          data-testid="pwd-modal"
        >
          <Card className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-blue-600" />
                Nouveau mot de passe
              </CardTitle>
              <p className="text-sm text-gray-500">
                Pour : <strong>{pwdTarget.email}</strong>
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitPwd} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Mot de passe (min. 8 caractères)
                  </label>
                  <div className="relative">
                    <Input
                      type={showPwd ? 'text' : 'password'}
                      value={newPwd}
                      onChange={(e) => setNewPwd(e.target.value)}
                      autoFocus
                      minLength={8}
                      required
                      className="pr-10"
                      placeholder="Nouveau mot de passe"
                      data-testid="input-new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((p) => !p)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setPwdTarget(null)}
                    disabled={pwdSaving}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={pwdSaving || newPwd.length < 8}
                    data-testid="btn-submit-new-password"
                  >
                    {pwdSaving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminUsersManagementPage;
